import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQueryClient, useQuery } from 'react-query'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import { motion } from 'framer-motion'
import {
  ArrowLeft,
  Building2,
  Mail,
  User,
  CreditCard,
  Save,
  Palette,
  Globe,
  Plus,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  ExternalLink,
  Settings as SettingsIcon,
  Briefcase
} from 'lucide-react'
import LoadingSpinner from '../common/LoadingSpinner'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card'
import { Button } from '../ui/button'
import { cn } from '../../utils/cn'
import { INDUSTRY_OPTIONS } from '../../utils/industrySchemas'

const EditClient = () => {
  const navigate = useNavigate()
  const { clientId } = useParams()
  const queryClient = useQueryClient()
  
  // Fetch client data
  const { data: client, isLoading: clientLoading } = useQuery(
    ['client', clientId],
    () => apiService.get(`/clients/profile?clientId=${clientId}`).then(res => res.data),
    { enabled: !!clientId }
  )

  // Fetch billing plans
  const { data: billingPlans } = useQuery(
    'billing-plans',
    () => apiService.get('/billing/plans').then(res => res.data)
  )

  // Fetch client domains
  const { data: clientDomains, refetch: refetchDomains } = useQuery(
    ['client-domains', clientId],
    () => apiService.get('/domains').then(res => {
      const domains = res.data?.domains || [];
      const filtered = domains.filter(d => d.client_id === clientId);
      return filtered;
    }),
    { enabled: !!clientId }
  )
  
  const { register, handleSubmit, watch, reset, setValue, formState: { errors } } = useForm()
  
  const subscriptionPlan = watch('subscription_plan')
  const isActive = watch('is_active')

  // Domain settings modal state
  const [showSettingsModal, setShowSettingsModal] = useState(false)
  const [selectedDomain, setSelectedDomain] = useState(null)
  const [domainSettings, setDomainSettings] = useState({
    ssl_enabled: false,
    contact_email: '',
    status: 'pending'
  })

  // Reset form when client data loads
  useEffect(() => {
    if (client) {
      reset({
        company_name: client.company_name,
        subscription_plan: client.subscription_plan || 'basic',
        edit_quota: client.edit_quota || client.monthly_edit_limit || 50,
        brand_colors: client.brand_colors?.[0] || '#3B82F6',
        is_active: client.is_active,
        industry: client.industry || 'general',
      })
    }
  }, [client, reset])

  // Update client mutation
  const updateClientMutation = useMutation(
    (clientData) => apiService.put('/clients/profile', { ...clientData, clientId }),
    {
      onSuccess: () => {
        toast.success('Client updated successfully!')
        queryClient.invalidateQueries(['client', clientId])
        queryClient.invalidateQueries('clients')
        navigate(`/admin/clients/${clientId}`)
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to update client')
      }
    }
  )

  // Add domain mutation
  const addDomainMutation = useMutation(
    (data) => apiService.post('/domains', { ...data, client_id: clientId }),
    {
      onSuccess: () => {
        toast.success('Domain added successfully!')
        refetchDomains()
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to add domain')
      }
    }
  )

  // Verify domain mutation
  const verifyDomainMutation = useMutation(
    (domainId) => apiService.post(`/domains/${domainId}/verify`),
    {
      onSuccess: (data) => {
        // Backend returns success response with message and domain object
        if (data.data.message && data.data.domain) {
          toast.success(data.data.message)
        } else {
          toast.error('Verification response was incomplete')
        }
        refetchDomains()
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Verification failed')
      }
    }
  )

  // Delete domain mutation
  const deleteDomainMutation = useMutation(
    (domainId) => apiService.delete(`/domains/${domainId}`),
    {
      onSuccess: () => {
        toast.success('Domain deleted successfully!')
        refetchDomains()
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to delete domain')
      }
    }
  )

  // Update domain mutation
  const updateDomainMutation = useMutation(
    ({ domainId, settings }) => apiService.patch(`/domains/${domainId}`, settings),
    {
      onSuccess: () => {
        toast.success('Domain settings updated successfully!')
        refetchDomains()
        setShowSettingsModal(false)
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to update domain settings')
      }
    }
  )

  const onSubmit = (data) => {
    // Create the client data structure
    const clientData = {
      company_name: data.company_name,
      subscription_plan: data.subscription_plan,
      edit_quota: parseInt(data.edit_quota),
      brand_colors: data.brand_colors ? [data.brand_colors] : [],
      is_active: Boolean(data.is_active),
      industry: data.industry || 'general',
    }
    
    updateClientMutation.mutate(clientData)
  }

  // Build plan options from /billing/plans (which already includes custom plans)
  const planOptions = billingPlans ? Object.entries(billingPlans).map(([id, plan]) => ({
    id,
    name: plan.name,
    description: `£${plan.price}/month${plan.custom ? ' · Custom' : ''}`,
    quota: plan.features.editsPerMonth,
    price: plan.price,
    type: plan.custom ? 'custom' : 'standard',
    features: [
      `${plan.features.sites === 'unlimited' ? 'Unlimited' : plan.features.sites} sites`,
      `${plan.features.editsPerMonth === 'unlimited' ? 'Unlimited' : plan.features.editsPerMonth} edits/month`,
      plan.features.storage,
      plan.features.support
    ]
  })) : []

  if (clientLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Loading client details..." />
      </div>
    )
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Client Not Found
        </h3>
        <p className="text-gray-500 dark:text-gray-400 mb-6">
          The client you're trying to edit doesn't exist.
        </p>
        <button
          onClick={() => navigate('/admin/clients')}
          className="btn-primary"
        >
          Back to Clients
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate(`/admin/clients/${clientId}`)}
          className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Edit Client
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Update {client.company_name}'s information and settings
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Client Form */}
        <div className="lg:col-span-2 space-y-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Company Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-3">
                    <div className="p-2 bg-accent-500/10 rounded-lg">
                      <Building2 className="h-5 w-5 text-accent-600 dark:text-accent-400" />
                    </div>
                    <span>Company Information</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    {...register('company_name', { required: 'Company name is required' })}
                    className="h-9 w-full px-3 border border-neutral-200 dark:border-[#2f2b41] rounded-md bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 dark:placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent"
                    placeholder="Acme Corporation"
                  />
                  {errors.company_name && (
                    <p className="text-error-500 dark:text-error-400 text-sm mt-1">{errors.company_name.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Industry <span className="text-error-500">*</span>
                  </label>
                  <select
                    {...register('industry')}
                    className={cn(
                      'h-9 w-full px-3 border border-neutral-200 dark:border-[#2f2b41] rounded-md',
                      'bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100',
                      'focus:ring-2 focus:ring-accent-500 focus:border-transparent',
                      'transition-all duration-200'
                    )}
                  >
                    {INDUSTRY_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.icon} {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Brand Color
                  </label>
                  <input
                    type="color"
                    {...register('brand_colors')}
                    className="h-10 w-full px-3 py-2 border border-neutral-200 dark:border-[#2f2b41] rounded-md bg-white dark:bg-[#221f2f] focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent"
                  />
                </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Domain Management */}
            <DomainManagementSection 
              clientDomains={clientDomains}
              onAddDomain={addDomainMutation.mutate}
              onVerifyDomain={verifyDomainMutation.mutate}
              onDeleteDomain={deleteDomainMutation.mutate}
              onUpdateDomain={updateDomainMutation.mutate}
              isAddingDomain={addDomainMutation.isLoading}
              isVerifyingDomain={verifyDomainMutation.isLoading}
              isDeletingDomain={deleteDomainMutation.isLoading}
              isUpdatingDomain={updateDomainMutation.isLoading}
              showSettingsModal={showSettingsModal}
              setShowSettingsModal={setShowSettingsModal}
              selectedDomain={selectedDomain}
              setSelectedDomain={setSelectedDomain}
              domainSettings={domainSettings}
              setDomainSettings={setDomainSettings}
            />

            {/* Subscription Plan */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-3">
                    <div className="p-2 bg-accent-500/10 rounded-lg">
                      <CreditCard className="h-5 w-5 text-accent-600 dark:text-accent-400" />
                    </div>
                    <span>Subscription Plan</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {planOptions.map((plan) => (
                    <label
                      key={plan.id}
                      className={`
                        relative cursor-pointer rounded-lg border p-4 transition-all
                        ${subscriptionPlan === plan.id
                          ? 'border-accent-500 bg-accent-50 dark:bg-accent-900/20'
                          : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600'
                        }
                      `}
                    >
                      <input
                        type="radio"
                        value={plan.id}
                        {...register('subscription_plan')}
                        className="sr-only"
                      />
                      <div className="space-y-2">
                        <h3 className="font-medium text-neutral-900 dark:text-neutral-100">
                          {plan.name}
                        </h3>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400">
                          {plan.description}
                        </p>
                        <p className="text-sm font-medium text-accent-600 dark:text-accent-400">
                          £{plan.price}/month
                        </p>
                        <p className="text-xs text-neutral-600 dark:text-neutral-400">
                          {plan.quota === 'unlimited' ? 'Unlimited' : plan.quota} edits
                        </p>
                        <ul className="text-xs text-neutral-500 dark:text-neutral-400 space-y-1">
                          {plan.features.map((feature, index) => (
                            <li key={index}>• {feature}</li>
                          ))}
                        </ul>
                      </div>
                    </label>
                  ))}
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Monthly Edit Quota
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="1000"
                    {...register('edit_quota', { 
                      required: 'Edit quota is required',
                      min: { value: 1, message: 'Minimum 1 edit' }
                    })}
                    className="h-9 w-full px-3 border border-neutral-200 dark:border-[#2f2b41] rounded-md bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 dark:placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent"
                    placeholder="50"
                  />
                  {errors.edit_quota && (
                    <p className="text-error-500 dark:text-error-400 text-sm mt-1">{errors.edit_quota.message}</p>
                  )}
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                    Override the default plan quota if needed
                  </p>
                </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Account Settings */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-3">
                    <div className="p-2 bg-accent-500/10 rounded-lg">
                      <User className="h-5 w-5 text-accent-600 dark:text-accent-400" />
                    </div>
                    <span>Account Settings</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                        Account Status
                      </label>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400">
                        Enable or disable this client's access to the system
                      </p>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={isActive}
                        onChange={(e) => setValue('is_active', e.target.checked)}
                        className="mr-2 h-4 w-4 text-accent-600 focus:ring-accent-500 border-neutral-300 dark:border-neutral-600 rounded bg-white dark:bg-neutral-800"
                      />
                      <span className="text-sm text-neutral-700 dark:text-neutral-300">
                        Active
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </form>
        </div>

        {/* Preview/Actions */}
        <div className="space-y-6">
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle>Current Client</CardTitle>
              </CardHeader>
              <CardContent>
              <div className="space-y-3 text-sm">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0 h-10 w-10">
                    <div className="h-10 w-10 rounded-full bg-accent-600 flex items-center justify-center">
                      <span className="text-white font-medium text-sm">
                        {client.user_name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="font-medium text-neutral-900 dark:text-neutral-100">
                      {client.user_name}
                    </p>
                    <p className="text-neutral-500 dark:text-neutral-400">
                      {client.email}
                    </p>
                  </div>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-neutral-500 dark:text-neutral-400">Plan:</span>
                  <span className="font-medium text-neutral-900 dark:text-neutral-100 capitalize">
                    {planOptions.find(p => p.id === (subscriptionPlan || client.subscription_plan))?.name || subscriptionPlan || client.subscription_plan}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-500 dark:text-neutral-400">Sites:</span>
                  <span className="font-medium text-neutral-900 dark:text-neutral-100">
                    {client.site_count || 0}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-500 dark:text-neutral-400">Edits Used:</span>
                  <span className="font-medium text-neutral-900 dark:text-neutral-100">
                    {client.edits_used || 0} / {client.edit_quota || client.monthly_edit_limit}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-500 dark:text-neutral-400">Status:</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    (isActive !== undefined ? isActive : client.is_active) 
                      ? 'bg-success-100 dark:bg-success-900/20 text-success-800 dark:text-success-200' 
                      : 'bg-error-100 dark:bg-error-900/20 text-error-800 dark:text-error-200'
                  }`}>
                    {(isActive !== undefined ? isActive : client.is_active) ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <Card className="card-elevated">
              <CardContent className="p-6 space-y-3">
                <Button
                  onClick={handleSubmit(onSubmit)}
                  disabled={updateClientMutation.isLoading}
                  className="w-full"
                  size="lg"
                >
                  {updateClientMutation.isLoading ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Update Client
                    </>
                  )}
                </Button>
                
                <Button
                  variant="outline"
                  onClick={() => navigate(`/admin/clients/${clientId}`)}
                  className="w-full border-neutral-200 dark:border-[#2f2b41] bg-white dark:bg-[#221f2f] text-neutral-700 dark:text-neutral-300"
                  size="lg"
                >
                  Cancel
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

// Domain Management Section Component
const DomainManagementSection = ({ 
  clientDomains, 
  onAddDomain, 
  onVerifyDomain, 
  onDeleteDomain,
  onUpdateDomain,
  isAddingDomain, 
  isVerifyingDomain, 
  isDeletingDomain,
  isUpdatingDomain,
  showSettingsModal,
  setShowSettingsModal,
  selectedDomain,
  setSelectedDomain,
  domainSettings,
  setDomainSettings
}) => {
  const [showAddForm, setShowAddForm] = useState(false)
  const [newDomain, setNewDomain] = useState('')
  const [newSubdomain, setNewSubdomain] = useState('')
  const [useSubdomain, setUseSubdomain] = useState(false)

  const handleAddDomain = () => {
    console.log('handleAddDomain called with:', { newDomain, useSubdomain, newSubdomain });
    if (!newDomain.trim()) {
      console.log('Domain is empty, returning');
      return;
    }

    const domainData = {
      domain: newDomain.toLowerCase().trim()
    }
    
    // Only include subdomain if it's actually provided
    if (useSubdomain && newSubdomain?.trim()) {
      domainData.subdomain = newSubdomain.toLowerCase().trim()
    }
    
    console.log('Calling onAddDomain with:', domainData);
    onAddDomain(domainData)

    // Reset form
    setNewDomain('')
    setNewSubdomain('')
    setUseSubdomain(false)
    setShowAddForm(false)
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified':
      case 'active':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />
      case 'dns_configured':
        return <AlertCircle className="h-4 w-4 text-blue-500" />
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />
      default:
        return <Clock className="h-4 w-4 text-gray-500" />
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified':
      case 'active':
        return 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-200'
      case 'pending':
        return 'bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-200'
      case 'dns_configured':
        return 'bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-200'
      case 'failed':
        return 'bg-error-100 text-error-800 dark:bg-error-900/20 dark:text-error-200'
      default:
        return 'bg-neutral-100 text-neutral-800 dark:bg-neutral-800 dark:text-neutral-200'
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.15 }}
    >
      <Card className="card-elevated">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-3">
              <div className="p-2 bg-accent-500/10 rounded-lg">
                <Globe className="h-5 w-5 text-accent-600 dark:text-accent-400" />
              </div>
              <span>Custom Domains</span>
            </CardTitle>
            <Button
              type="button"
              onClick={() => {
                console.log('Add Domain button clicked, current showAddForm:', showAddForm);
                setShowAddForm(!showAddForm);
              }}
              size="sm"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Domain
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
        {/* Add Domain Form */}
        {showAddForm && (
          <div className="bg-neutral-50 dark:bg-[#2a2538] rounded-lg p-4 space-y-4 border border-neutral-200 dark:border-[#3a3548]">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Domain Name
              </label>
              <input
                type="text"
                value={newDomain || ''}
                onChange={(e) => setNewDomain(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddDomain()
                  }
                }}
                placeholder="example.com"
                className="h-9 w-full px-3 border border-neutral-200 dark:border-[#3a3548] rounded-md bg-white dark:bg-[#1e1b2e] text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 dark:placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent transition-colors duration-200"
                required
              />
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                Enter domain without http:// or www
              </p>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="useSubdomain"
                checked={useSubdomain}
                onChange={(e) => setUseSubdomain(e.target.checked)}
                className="rounded border-neutral-300 dark:border-neutral-600 text-accent-600 focus:ring-accent-500 bg-white dark:bg-neutral-800"
              />
              <label htmlFor="useSubdomain" className="ml-2 text-sm text-neutral-700 dark:text-neutral-300">
                Use a subdomain instead
              </label>
            </div>

            {useSubdomain && (
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Subdomain
                </label>
                <div className="flex border border-neutral-200 dark:border-[#3a3548] rounded-md overflow-hidden focus-within:ring-2 focus-within:ring-accent-500 focus-within:border-transparent transition-all duration-200">
                  <input
                    type="text"
                    value={newSubdomain || ''}
                    onChange={(e) => setNewSubdomain(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        handleAddDomain()
                      }
                    }}
                    placeholder="www"
                    className="h-9 flex-1 px-3 bg-white dark:bg-[#1e1b2e] text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 dark:placeholder:text-neutral-400 focus:outline-none border-0"
                    required={useSubdomain}
                  />
                  <span className="inline-flex items-center px-3 bg-neutral-50 dark:bg-[#2a2538] text-neutral-500 dark:text-neutral-400 text-sm border-l border-neutral-200 dark:border-[#3a3548]">
                    .{newDomain || 'example.com'}
                  </span>
                </div>
              </div>
            )}

            <div className="flex space-x-2">
              <Button
                type="button"
                onClick={handleAddDomain}
                disabled={isAddingDomain || !newDomain.trim()}
                size="sm"
              >
                {isAddingDomain ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  'Add Domain'
                )}
              </Button>
              <Button
                type="button"
                onClick={() => setShowAddForm(false)}
                variant="outline"
                className="border-neutral-200 dark:border-[#2f2b41] bg-white dark:bg-[#221f2f] text-neutral-700 dark:text-neutral-300"
                size="sm"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Domains List */}
        {clientDomains && clientDomains.length > 0 ? (
          <div className="space-y-3">
            {clientDomains.map((domain) => {
              const fullDomain = domain.subdomain ? `${domain.subdomain}.${domain.domain}` : domain.domain
              return (
                <div key={domain.id} className="flex items-center justify-between p-3 border border-neutral-200 dark:border-[#3a3548] rounded-lg hover:bg-neutral-50 dark:hover:bg-[#2a2538] transition-colors duration-200">
                  <div className="flex items-center space-x-3">
                    <Globe className="h-5 w-5 text-neutral-400" />
                    <div>
                      <div className="font-medium text-neutral-900 dark:text-neutral-100">
                        {fullDomain}
                      </div>
                      {domain.assigned_sites && domain.assigned_sites.length > 0 && (
                        <div className="text-xs text-accent-600 dark:text-accent-400 mt-1">
                          Used by {domain.assigned_sites.length} site{domain.assigned_sites.length > 1 ? 's' : ''}: {domain.assigned_sites.map(s => s.name).join(', ')}
                        </div>
                      )}
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(domain.status)}`}>
                          {getStatusIcon(domain.status)}
                          <span className="ml-1 capitalize">{domain.status.replace('_', ' ')}</span>
                        </span>
                        {!!domain.ssl_enabled && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-200">
                            SSL
                          </span>
                        )}
                      </div>
                      {domain.error_message && (
                        <p className="text-xs text-red-600 mt-1">{domain.error_message}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {domain.status === 'verified' && domain.azure_published_url && (
                      <a
                        href={domain.azure_published_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:text-primary-700"
                        title="View Live Site"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                    
                    {(domain.status === 'pending' || domain.status === 'failed') && (
                      <button
                        onClick={() => onVerifyDomain(domain.id)}
                        disabled={isVerifyingDomain}
                        className="px-3 py-1.5 text-xs font-medium rounded-md bg-accent-600 hover:bg-accent-700 text-white disabled:bg-neutral-400 disabled:cursor-not-allowed transition-colors duration-200"
                        title="Verify Domain"
                      >
                        {isVerifyingDomain ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          'Verify'
                        )}
                      </button>
                    )}
                    
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        setSelectedDomain(domain)
                        setDomainSettings({
                          ssl_enabled: Boolean(domain.ssl_enabled),
                          contact_email: domain.contact_email || '',
                          status: domain.status || 'pending'
                        })
                        setShowSettingsModal(true)
                      }}
                      className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors duration-200"
                      title="Domain Settings"
                    >
                      <SettingsIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="text-center py-6 text-gray-500 dark:text-gray-400">
            <Globe className="h-8 w-8 mx-auto mb-2 text-gray-400" />
            <p className="text-sm">No custom domains configured</p>
            <p className="text-xs">Add a domain to connect this client's website to their own URL</p>
          </div>
        )}
        </CardContent>
      </Card>

      {/* Domain Settings Modal */}
      {showSettingsModal && selectedDomain && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowSettingsModal(false)
            }
          }}
        >
          <div 
            className="modal-content bg-white dark:bg-[#221f2f] rounded-lg p-6 max-w-lg w-full mx-4 border border-neutral-200 dark:border-[#2f2b41]"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
              Domain Settings - {selectedDomain.subdomain ? `${selectedDomain.subdomain}.${selectedDomain.domain}` : selectedDomain.domain}
            </h3>
            
            <div className="space-y-6">
              {/* Domain Info */}
              <div className="p-4 bg-neutral-50 dark:bg-[#2f2b41] rounded-lg">
                <h4 className="font-medium text-neutral-900 dark:text-neutral-100 mb-2">Domain Information</h4>
                <div className="text-sm text-neutral-600 dark:text-neutral-400 space-y-1">
                  <div>Domain: <span className="font-mono">{selectedDomain.subdomain ? `${selectedDomain.subdomain}.${selectedDomain.domain}` : selectedDomain.domain}</span></div>
                  <div>Created: {new Date(selectedDomain.created_at).toLocaleDateString()}</div>
                  {selectedDomain.verified_at && (
                    <div>Verified: {new Date(selectedDomain.verified_at).toLocaleDateString()}</div>
                  )}
                </div>
              </div>

              {/* SSL Settings */}
              <div>
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={domainSettings.ssl_enabled}
                    onChange={(e) => setDomainSettings(prev => ({ ...prev, ssl_enabled: e.target.checked }))}
                    className="w-4 h-4 text-accent-600 bg-neutral-100 border-neutral-300 rounded focus:ring-accent-500 dark:bg-neutral-700 dark:border-neutral-600"
                  />
                  <div>
                    <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">Enable SSL Certificate</span>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">Automatically provision and manage SSL certificate</p>
                  </div>
                </label>
              </div>

              {/* Contact Email */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Contact Email
                </label>
                <input
                  type="email"
                  value={domainSettings.contact_email}
                  onChange={(e) => setDomainSettings(prev => ({ ...prev, contact_email: e.target.value }))}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-[#2f2b41] rounded-md bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent"
                  placeholder="contact@example.com"
                />
              </div>

              {/* Domain Status */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Domain Status
                </label>
                <select
                  value={domainSettings.status}
                  onChange={(e) => setDomainSettings(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-[#2f2b41] rounded-md bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent"
                >
                  <option value="pending">Pending</option>
                  <option value="dns_configured">DNS Configured</option>
                  <option value="verified">Verified</option>
                  <option value="active">Active</option>
                  <option value="failed">Failed</option>
                </select>
              </div>

              {/* DNS Information */}
              <div className="p-4 bg-neutral-50 dark:bg-[#2f2b41] rounded-lg">
                <h4 className="font-medium text-neutral-900 dark:text-neutral-100 mb-2">DNS Configuration</h4>
                <div className="text-sm text-neutral-600 dark:text-neutral-400 space-y-2">
                  <div className="font-mono text-xs bg-white dark:bg-[#221f2f] p-2 rounded border">
                    <div>Type: CNAME</div>
                    <div>Name: {selectedDomain.subdomain || '@'}</div>
                    <div>Value: your-cdn-provider.com</div>
                  </div>
                  <p className="text-xs">Point your domain to the above CNAME record for proper configuration.</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-between items-center pt-4 border-t border-neutral-200 dark:border-[#2f2b41]">
                <div>
                  {(selectedDomain.status === 'pending' || selectedDomain.status === 'failed') && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        onVerifyDomain(selectedDomain.id)
                        setShowSettingsModal(false)
                      }}
                      disabled={isVerifyingDomain}
                      className="border-accent-300 dark:border-accent-600 text-accent-600 dark:text-accent-400 hover:bg-accent-50 dark:hover:bg-accent-900/20"
                    >
                      {isVerifyingDomain ? <LoadingSpinner size="sm" /> : 'Force Verify'}
                    </Button>
                  )}
                </div>
                <div className="flex space-x-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowSettingsModal(false)}
                    className="border-neutral-200 dark:border-[#2f2b41] bg-white dark:bg-[#221f2f] text-neutral-700 dark:text-neutral-300"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      const updates = {}
                      if (domainSettings.ssl_enabled !== selectedDomain.ssl_enabled) {
                        updates.ssl_enabled = domainSettings.ssl_enabled
                      }
                      if (domainSettings.contact_email !== selectedDomain.contact_email) {
                        updates.contact_email = domainSettings.contact_email
                      }
                      if (domainSettings.status !== selectedDomain.status) {
                        updates.status = domainSettings.status
                      }

                      if (Object.keys(updates).length > 0) {
                        onUpdateDomain({ domainId: selectedDomain.id, settings: updates })
                      } else {
                        toast('No changes to save')
                        setShowSettingsModal(false)
                      }
                    }}
                    disabled={isUpdatingDomain}
                  >
                    {isUpdatingDomain ? <LoadingSpinner size="sm" /> : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  )
}

export default EditClient